import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PawPrint, Heart } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { MaskLine, Reveal } from "@/components/Reveal";
import { api } from "@/lib/helpers";

function TributeCard({ t, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`mb-6 break-inside-avoid ${i % 2 === 1 ? "sm:mt-10" : ""}`}
      data-testid={`tribute-card-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="group overflow-hidden rounded-[2rem] bg-white border border-stone-200 shadow-sm transition-all duration-500 hover:shadow-2xl hover:-translate-y-1">
        <div className={`relative overflow-hidden ${i % 3 === 0 ? "rounded-tl-[3.5rem]" : "rounded-tr-[3.5rem]"}`}>
          <img src={t.image} alt={`Tribute to ${t.name}`} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-transparent" />
          <span className="absolute bottom-4 right-4 rounded-full bg-sand/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-pine">
            Forever loved
          </span>
        </div>
        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-2">
            <PawPrint className="h-4 w-4 text-gold" />
            {t.years && <p className="font-mono text-xs tracking-[0.18em] text-gold-deep">{t.years}</p>}
          </div>
          <h3 className="mt-2 font-serif text-2xl text-charcoal">{t.name}</h3>
          {t.story && <p className="mt-3 text-sm leading-relaxed text-bark">{t.story}</p>}
        </div>
      </div>
    </motion.div>
  );
}

export default function Tributes() {
  const [tributes, setTributes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tributes").then(({ data }) => setTributes(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main className="bg-sand">
      <Nav onNavigateHome />

      <section className="relative bg-pine-deep grain overflow-hidden py-24 sm:py-32 lg:py-36">
        <img src="/sections/tributes_heaven.webp"
          alt="Dogs and cats gazing toward heavenly light in the clouds"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/45 to-pine-deep/90" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1 }} className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-charcoal/40 backdrop-blur-sm border border-gold/40">
            <Heart className="h-7 w-7 text-gold-soft" />
          </motion.div>
          <p className="dark-eyebrow mt-6">Rescue tributes</p>
          <h1 className="mt-4 font-serif text-3xl leading-tight text-sand sm:text-5xl lg:text-6xl tracking-tight">
            <MaskLine delay={0.3}>Gone from our sight,</MaskLine>
            <MaskLine delay={0.45}>never from our <span className="italic text-gold-soft">hearts</span>.</MaskLine>
          </h1>
          <MaskLine delay={0.65} className="mx-auto mt-6 max-w-xl">
            <span className="text-base sm:text-lg leading-relaxed text-sand/75">
              A quiet corner of the mountain where we remember the dogs and cats who touched our lives
              and left paw prints on our hearts.
            </span>
          </MaskLine>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {loading && <p className="py-16 text-center font-mono text-sm text-bark">Lighting the candles…</p>}
          {!loading && tributes.length > 0 && (
            <div className="columns-1 gap-6 sm:columns-2 lg:columns-3" data-testid="tributes-gallery">
              {tributes.map((t, i) => <TributeCard key={t.id} t={t} i={i} />)}
            </div>
          )}
          {!loading && !tributes.length && (
            <div className="mx-auto max-w-md rounded-3xl bg-white border border-stone-200 p-10 text-center" data-testid="tributes-empty">
              <PawPrint className="mx-auto h-8 w-8 text-gold" />
              <h2 className="mt-4 font-serif text-2xl text-charcoal">Their stories are still being written.</h2>
              <p className="mt-3 text-sm text-bark">Tributes added by our team will appear here.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </main>
  );
}
